import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FieldProps } from "react-jsonschema-form";
import styled from "styled-components";
import { useGrpcClient } from "../../../lib/grpc";
import {
  FindOrganizationsRequest,
  GetOrganizationRequest,
  Organization,
} from "../../../gen/witbooking/loyalty/organization/v1/organization_api_pb";
import { OrganizationAPIPromiseClient } from "../../../gen/witbooking/loyalty/organization/v1/organization_api_grpc_web_pb";
import { useLoyaltyBrand } from "../../../components/loyalty-brand-provider";
import Selector from "../../../components/selector";

const StyledSelector = styled(Selector)`
  max-width: 200px;
`;

export function LoyaltyOrganizationSelector({
  formData,
  onChange,
  rawErrors,
  disabled,
  id,
  schema
}: FieldProps) {
  const brandId = useLoyaltyBrand()?.externalSystemId || "";

  const [organizations, setOrganizations] = useState<Organization.AsObject[] | undefined>();

  const organizationClient = useGrpcClient(OrganizationAPIPromiseClient);

  const mapOrganizationsData = (organizations: Organization.AsObject[]) => {
    return organizations.map(it => ({
      value: {
        id: it.id,
        ticker: it.ticker,
        organizationType: it.company ? 1 : 2,
      },
      label: it.name,
    }));
  }

  const getOrganization = useCallback(async (id: string) => {
    const request = new GetOrganizationRequest();
    request.setBrandId(brandId);
    request.setId(id);

    const response = await organizationClient.getOrganization(request);
    if (response) {
      const res = response.toObject();
      return res.organization;
    }
  }, [brandId, organizationClient]);

  const findOrganization = useCallback(async (organizationName: string) => {
    const request = new FindOrganizationsRequest();
    request.setBrandId(brandId);
    request.getFiltersMap().set("name", organizationName);

    const response = await organizationClient.findOrganizations(request);
    if (response) {
      const res = response.toObject();
      return res.organizationsList.length > 0 ? mapOrganizationsData(res.organizationsList) : [];
    }
  }, [brandId, organizationClient]);

  const getBrandOrganizations = useCallback(async (brandId: string) => {
    const request = new FindOrganizationsRequest();
    request.setBrandId(brandId);

    const response = await organizationClient.findOrganizations(request);
    if (response) {
      const res = response.toObject();
      return res.organizationsList;
    }
  }, [organizationClient])

  useEffect(() => {
    const asyncFun = async () => {
      if (formData && formData[0]) {
        const organization = await getOrganization(formData[0].id);
        setOrganizations([organization!]);
      } else if (brandId) {
        const organizations = await getBrandOrganizations(brandId)
        setOrganizations(organizations);
      }
    };
    asyncFun();
  }, [brandId, formData, getOrganization, getBrandOrganizations]);

  const isValidOrganizationName = useCallback((name: string) => name && name.length >= 3, []);

  const organizationsOptions = useMemo(() => {
    return organizations?.length ? mapOrganizationsData(organizations) : [];
  }, [organizations]);

  const handleInputChange = async (organizationName: string) => {
    if (isValidOrganizationName(organizationName)) {
      return await findOrganization(organizationName)
    }
  };

  const handleChange = useCallback(
    organization => onChange([organization.value]),
    [onChange]
  );

  const organizationValue = useMemo(() => {
    if (formData && formData[0]) {
      return organizationsOptions.find(it => it?.value?.id === formData[0].id);
    }
  }, [organizationsOptions, formData]);

  return (
    <>
      <label>{schema?.title}</label>
      <StyledSelector 
        async={true}
        value={organizationValue}
        onChange={handleChange}
        defaultOptions={organizationsOptions}
        loadOptions={handleInputChange}
      />
    </>
  );
}
